// @flow
/* eslint-disable arrow-body-style */

import React, { useEffect, useMemo, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion/dist/framer-motion';
import classNames from 'classnames';
import Icon from 'components/_particles/icon/react/Icon';
import Tippy from '@tippyjs/react';
import { followCursor } from 'tippy.js';
import { t } from 'javascripts/utils/withTranslation';
import type { Tree, Wording } from '../types';

type Props = {
  tile: Tree,
  onClick: (tile: Tree) => void,
  /** If we are on a sub level the offset pushes the tiles down by the height of its header representing the parent tile */
  offset?: number,
  variant?: 'absolute' | 'relative',
  wording?: Wording,
};

export const printTileValue = (
  tile?: Tree,
  variant: 'absolute' | 'relative',
  lang: string
): string => {
  if (variant === 'relative') {
    return new Intl.NumberFormat(lang, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      unitDisplay: 'narrow',
    }).format(tile?.data.partial || 0);
  }
  return new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: 'EUR',
    // results in 1 Mio. instead of 1.000.000, but keeps 1.000 in smaller numbers
    notation: (tile?.data.absolute || 0) >= 1000000 ? 'compact' : undefined,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    unitDisplay: 'narrow',
  }).format(tile?.data.absolute || 0);
};

const Tile = ({
  tile,
  onClick,
  offset = 0,
  variant = 'absolute',
  wording,
}: Props): React$MixedElement => {
  const { lang } = document.documentElement || { lang: 'de' };
  const [showTooltip, setShowTooltip] = React.useState(false);
  const timer = useRef<any>(null);
  const printValue = useMemo(
    () => printTileValue(tile, variant, lang),
    [tile, variant, lang]
  );

  const zeromatcher = useMemo(
    () => printTileValue(undefined, variant, lang),
    [tile, variant, lang]
  );

  const handleMouseEnter = () => {
    // try to catch all tiles with long titles and small width
    if (
      tile.x1 - tile.x0 <= 120 ||
      (tile.data.title?.length >= 38 && tile.x1 - tile.x0 <= 300) ||
      printValue === zeromatcher
    )
      timer.current = setInterval(() => setShowTooltip(true), 1000);
  };
  const handleMouseLeave = () => {
    clearInterval(timer.current);
    setShowTooltip(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -400, scale: 0.8 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 200, scale: 1.2 }}
      transition={{ duration: 0.6, type: 'spring' }}
      key={tile.data.id}
      layoutScroll
    >
      <Tippy
        visible={showTooltip}
        content={
          printValue === zeromatcher
            ? `${tile.data.title} - ${
                wording?.tile?.zeroTooltip ||
                t(
                  'Der Wert ist so gering, dass er nicht dargestellt werden kann.'
                )
              }`
            : `${printValue} - ${tile.data.title}`
        }
        placement="bottom"
        animation="shift-away"
        arrow={false}
        theme="bw"
        plugins={[followCursor]}
        followCursor
        className="tax-calculator__tooltip"
      >
        <div
          style={{
            transform: `translate(${tile.x0}px, ${tile.y0 + offset}px)`,
            width: tile.x1 - tile.x0,
            height: tile.y1 - tile.y0,
            cursor: tile.children ? 'pointer' : 'default',
          }}
          key={tile.data.id}
          id={tile.data.id}
          onClick={() => onClick(tile)}
          className="tax-calculator__tile"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="tax-calculator__tile-inner">
            <div className="tax-calculator__title">
              <div className="tax-calculator__value">
                {tile.data.icon && (
                  <Icon
                    className="tax-calculator__icon"
                    icon={tile.data.icon}
                  />
                )}
                <span
                  className={classNames(
                    'tax-calculator__currency',
                    printValue === zeromatcher &&
                      'tax-calculator__currency--empty'
                  )}
                >
                  {printValue === zeromatcher
                    ? wording?.tile?.zero || t('Wert zu gering')
                    : printValue}
                </span>
              </div>
              <div className="tax-calculator__title-text">
                <span>{tile.data.title}</span>
              </div>
            </div>
            <div className="tax-calculator__toggle">
              {tile.children && (
                <button
                  type="button"
                  onClick={() => onClick(tile)}
                  className="button button--icon button--secondary button--small tax-calculator__toggle-button"
                >
                  <Icon icon="bw-plus-large-line" />
                  <span className="u-hidden-visually">
                    {t('Betrag weiter aufschlüsseln')}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </Tippy>
    </motion.div>
  );
};

export default Tile;
